import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import { useTranslation } from "react-i18next";
import codService from "../../../services/CodService";
import {
  CodInvoiceSelection,
  CodOrderInvoices,
  CodPostPaymentResponse,
} from "../../../types/codSalesRep";
import {
  EnrichedCodPayment,
  flattenPayableInvoices,
  codInvoiceStatusKey,
  formatCodAmount,
  sumInvoiceRemaining,
} from "../../../utils/codUtils";
import PostCodPaymentModal from "./PostCodPaymentModal";
import CodInvoiceTree from "./CodInvoiceTree";
import { showToast } from "../../component/common/toastNotification";
import {
  CodCustomerOption,
  matchesCodCustomer,
} from "./codCustomerUtils";
import { glassCardSx, codTableSx, codTableHeadCellSx, primaryBtnSx, iconBtnSx } from "./codStyles";
import CodStatCard from "../../../components/ui/CodStatCard";
import StatusChip from "../../../components/ui/StatusChip";
import EmptyState from "../../../components/ui/EmptyState";
import TableSkeleton from "../../../components/ui/TableSkeleton";
import CodSectionHeader from "../../../components/ui/CodSectionHeader";

interface CodPaymentsTabProps {
  refreshKey?: number;
  readOnly?: boolean;
  customer?: CodCustomerOption | null;
  onPosted?: () => void;
}

const CodPaymentsTab: React.FC<CodPaymentsTabProps> = ({
  refreshKey = 0,
  readOnly = false,
  customer = null,
  onPosted,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [retryingId, setRetryingId] = useState("");
  const [invoiceOrders, setInvoiceOrders] = useState<CodOrderInvoices[]>([]);
  const [payments, setPayments] = useState<EnrichedCodPayment[]>([]);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [blockedRetryIds, setBlockedRetryIds] = useState<string[]>([]);
  const [postResult, setPostResult] = useState<CodPostPaymentResponse | null>(null);

  const customerId = customer?.customerId ?? "";

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Orders → lines → Sage invoices for this customer (outstanding only), plus payment history.
      const [orders, paymentRows] = await Promise.all([
        customerId
          ? codService.listInvoiceOrders({ customerId, includePaid: false })
          : Promise.resolve([] as CodOrderInvoices[]),
        codService.listPayments(),
      ]);
      setInvoiceOrders(orders);
      setPayments(paymentRows);
      // Keep only selections that are still payable after a reload.
      const payable = new Set(flattenPayableInvoices(orders).map((invoice) => invoice.invoiceId));
      setSelectedInvoiceIds((prev) => prev.filter((id) => payable.has(id)));
    } catch {
      showToast(t("codSalesRep.loadFailed"), "error");
    } finally {
      setLoading(false);
    }
  }, [customerId, t]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  const payableInvoices = useMemo(() => flattenPayableInvoices(invoiceOrders), [invoiceOrders]);

  const filteredPayments = useMemo(
    () => payments.filter((payment) => matchesCodCustomer(payment, customer ?? null)),
    [payments, customer]
  );

  const selectedInvoices = payableInvoices.filter((invoice) =>
    selectedInvoiceIds.includes(invoice.invoiceId)
  );

  // The amount to collect is the selected invoices' remainingAmount — never the line/order total.
  const selectedTotal = sumInvoiceRemaining(selectedInvoices);

  const toggleInvoice = (invoiceId: string) => {
    setSelectedInvoiceIds((prev) =>
      prev.includes(invoiceId) ? prev.filter((id) => id !== invoiceId) : [...prev, invoiceId]
    );
  };

  const handlePostPayment = async (
    documentReceiptNumber: string,
    selections: CodInvoiceSelection[]
  ) => {
    if (selections.length === 0) return;
    setPosting(true);
    try {
      const result = await codService.postPayment({
        documentReceiptNumber,
        invoices: selections,
      });
      setPostResult(result);
      const hasIssues = result.failed.length > 0 || result.skipped.length > 0;
      if (hasIssues) {
        showToast(t("codSalesRep.paymentPostedWithIssues"), "info");
      } else {
        showToast(
          t("codSalesRep.paymentPosted", {
            ids: result.transactionIds.join(", ") || "—",
          }),
          "success"
        );
      }
      setModalOpen(false);
      setSelectedInvoiceIds([]);
      await loadData();
      onPosted?.();
    } catch {
      // http interceptor shows API error
    } finally {
      setPosting(false);
    }
  };

  const handleRetry = async (transactionId: string) => {
    setRetryingId(transactionId);
    try {
      const result = await codService.retryPayment(transactionId);
      if (result.invoiceNotIssued) {
        setBlockedRetryIds((prev) =>
          prev.includes(transactionId) ? prev : [...prev, transactionId]
        );
        showToast(result.message || t("codSalesRep.invoiceNotIssued"), "info");
        return;
      }
      showToast(result.message || t("codSalesRep.retryQueued"), "success");
      await loadData();
    } catch {
      // handled by interceptor
    } finally {
      setRetryingId("");
    }
  };

  if (!customer) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {t("codSalesRep.selectCustomerFirst")}
      </Alert>
    );
  }

  if (loading) {
    return <TableSkeleton rows={6} columns={6} />;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <CodStatCard
          label={t("codSalesRep.awaitingPayment")}
          value={payableInvoices.length}
          tone="warning"
          icon={<HourglassTopRoundedIcon />}
        />
        <CodStatCard
          label={t("codSalesRep.paymentsCreated")}
          value={filteredPayments.length}
          tone="success"
          icon={<CloudDoneRoundedIcon />}
        />
      </Box>

      {postResult && (postResult.failed.length > 0 || postResult.skipped.length > 0) && (
        <Alert
          severity={postResult.failed.length > 0 ? "warning" : "info"}
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setPostResult(null)}
        >
          {postResult.skipped.map((row) => (
            <Typography key={`skip-${row.orderId}`} variant="body2">
              {t("codSalesRep.skippedInvoices", {
                order: row.orderNumber || row.orderId,
                invoices: row.invoiceNumbers || "—",
                reason: row.message || row.reason,
              })}
            </Typography>
          ))}
          {postResult.failed.map((row) => (
            <Typography key={`fail-${row.orderId}`} variant="body2">
              {t("codSalesRep.failedInvoices", {
                order: row.orderNumber || row.orderId,
                invoices: row.invoiceNumbers || "—",
                reason: row.message || row.reason,
              })}
            </Typography>
          ))}
          {postResult.orders.flatMap((row) =>
            (row.invoices ?? []).map((invoice) => (
              <Typography key={`ok-${row.orderId}-${invoice.invoiceId}`} variant="body2">
                {t("codSalesRep.postedInvoiceState", {
                  invoice: invoice.invoiceNumber,
                  paid: formatCodAmount(invoice.amountPaidNow),
                  remaining: formatCodAmount(invoice.remainingAmount),
                  status: t(codInvoiceStatusKey(invoice.paymentStatus)),
                })}
              </Typography>
            ))
          )}
        </Alert>
      )}

      {!readOnly && (
        <Box sx={{ ...glassCardSx, p: 2.5, mb: 3, borderRadius: "14px" }}>
          <CodSectionHeader
            title={t("codSalesRep.invoicesToCollect")}
            helper={t("codSalesRep.invoicesToCollectHint")}
            action={
              <Button
                variant="contained"
                disabled={selectedInvoices.length === 0}
                onClick={() => setModalOpen(true)}
                sx={primaryBtnSx}
              >
                {t("codSalesRep.postPayment")} ({formatCodAmount(selectedTotal)})
              </Button>
            }
          />
          {selectedInvoices.length > 0 && (
            <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1.5 }}>
              {t("codSalesRep.selectedInvoices", { count: selectedInvoices.length })}
            </Typography>
          )}
          {invoiceOrders.length === 0 ? (
            <EmptyState
              title={t("codSalesRep.noPayableInvoices")}
              description={t("codSalesRep.noPayableInvoicesDesc")}
            />
          ) : (
            <CodInvoiceTree
              orders={invoiceOrders}
              selectedInvoiceIds={selectedInvoiceIds}
              onToggleInvoice={toggleInvoice}
            />
          )}
        </Box>
      )}

      <Box sx={{ ...glassCardSx, p: 2.5, borderRadius: "14px" }}>
        <CodSectionHeader
          title={t("codSalesRep.paymentHistory")}
          helper={t("codSalesRep.paymentsHint")}
        />
        <Table size="small" sx={codTableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={codTableHeadCellSx}>{t("codSalesRep.paymentReference")}</TableCell>
              <TableCell sx={codTableHeadCellSx}>{t("codSalesRep.customer")}</TableCell>
              <TableCell sx={codTableHeadCellSx}>{t("codSalesRep.receiptNumber")}</TableCell>
              <TableCell sx={codTableHeadCellSx}>{t("codSalesRep.sagePayment")}</TableCell>
              <TableCell align="right" sx={codTableHeadCellSx}>
                {t("common.amount")}
              </TableCell>
              <TableCell sx={codTableHeadCellSx}>{t("common.status")}</TableCell>
              {!readOnly && (
                <TableCell align="right" sx={codTableHeadCellSx}>
                  {t("common.actions")}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPayments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={readOnly ? 6 : 7}>
                    <EmptyState
                      title={t("codSalesRep.emptyPaymentsTitle")}
                      description={t("codSalesRep.emptyPaymentsDesc")}
                    />
                </TableCell>
              </TableRow>
            ) : (
              filteredPayments.map((payment) => (
                <TableRow key={payment.id || payment.transactionId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {payment.paymentReference || payment.orderNumber || "—"}
                    </Typography>
                    {payment.paymentReference && payment.orderNumber && (
                      <Typography variant="caption" color="text.secondary">
                        {payment.orderNumber}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      {payment.customerCode || "—"}
                    </Typography>
                    {payment.customerName && payment.customerName !== payment.customerCode && (
                      <Typography variant="caption" color="text.secondary">
                        {payment.customerName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{payment.documentReceiptNumber || "—"}</TableCell>
                  <TableCell>
                    {payment.isSynced && payment.sagePaymentNumber
                      ? payment.sagePaymentNumber
                      : "—"}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatCodAmount(payment.amount, payment.currency)}
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      status={payment.isSynced ? "paid" : "pendingSageSync"}
                      label={
                        payment.isSynced
                          ? t("codSalesRep.paid")
                          : t("codSalesRep.pendingSageSync")
                      }
                    />
                  </TableCell>
                  {!readOnly && (
                    <TableCell align="right">
                      {!payment.isSynced &&
                        !blockedRetryIds.includes(payment.transactionId) && (
                        <Tooltip title={t("codSalesRep.retrySync")}>
                        <IconButton
                          size="small"
                          aria-label={t("codSalesRep.retrySync")}
                          disabled={retryingId === payment.transactionId}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRetry(payment.transactionId);
                          }}
                          sx={iconBtnSx}
                        >
                          {retryingId === payment.transactionId ? (
                            <CircularProgress size={18} />
                          ) : (
                            <RefreshIcon fontSize="small" />
                          )}
                        </IconButton>
                        </Tooltip>
                      )}
                      {blockedRetryIds.includes(payment.transactionId) && (
                        <Typography variant="caption" color="text.secondary">
                          {t("codSalesRep.waitingForInvoice")}
                        </Typography>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      {!readOnly && (
        <PostCodPaymentModal
          open={modalOpen}
          invoices={selectedInvoices}
          loading={posting}
          onClose={() => setModalOpen(false)}
          onSubmit={handlePostPayment}
          onRemoveInvoice={toggleInvoice}
        />
      )}
    </Box>
  );
};

export default CodPaymentsTab;
